import { IRelistingRule } from "../models/listingOptimisation/IListingInsights";
import PriceFormat from "./priceFormat";

/**
 * One line on what the category's auto-relist rule does, e.g.
 * "Auto-relist every 30d · −5% per cycle · floor £120.00". The floor reads "floor from category"
 * when the rule has none, and the reduction is left out when the rule does not reduce.
 */
export function describeRelistingRule(
  rule: IRelistingRule | null | undefined,
  options: { includeName?: boolean } = {}
): string | null {
  if (!rule) return null;

  const parts: string[] = [`Auto-relist every ${rule.reductionInterval}d`];

  if (rule.useReduction && rule.reduction > 0) {
    parts.push(
      rule.isFixedReduction
        ? `−${PriceFormat(rule.reduction)} per cycle`
        : `−${rule.reduction}% per cycle`
    );
  }

  parts.push(rule.minimumPrice != null && rule.minimumPrice > 0 ? `floor ${PriceFormat(rule.minimumPrice)}` : "floor from category");

  if (options.includeName && rule.name) parts.push(`rule "${rule.name}"`);

  return parts.join(" · ");
}

/**
 * The rule badge for a page of rows: the one rule when every row with a rule shares it, a count
 * when they differ, and null when no row has one.
 */
export function summariseRelistingRules(rules: (IRelistingRule | null | undefined)[]): {
  text: string;
  distinct: number;
} | null {
  const present = rules.filter((rule): rule is IRelistingRule => !!rule);
  if (present.length === 0) return null;

  const distinct = new Map<string, IRelistingRule>();
  present.forEach((rule) => distinct.set(rule.id ?? rule.name ?? describeRelistingRule(rule)!, rule));

  if (distinct.size === 1) {
    return { text: describeRelistingRule(present[0])!, distinct: 1 };
  }

  return { text: `Auto-relist · ${distinct.size} category rules on this page`, distinct: distinct.size };
}
